// Library / Navigator Types

import type { NodeType, Domain } from './index'

export type LibraryViewMode = 'table' | 'cards' | 'timeline'

export type LibrarySortField = 'name' | 'type' | 'domain' | 'level' | 'created_at' | 'updated_at' | 'strength'
export type LibrarySortDirection = 'asc' | 'desc'

export interface LibraryFilters {
  search: string
  types: NodeType[]
  domains: string[]
  minLevel: number
  maxLevel: number
  dateRange?: {
    from: Date
    to: Date
  }
}

export interface LibrarySort {
  field: LibrarySortField
  direction: LibrarySortDirection
}

export interface LibraryItem {
  id: string
  name: string
  type: NodeType
  content: string
  domain_id?: string
  domain?: Domain
  level: number
  strength: number
  created_at: string
  updated_at?: string
  ingestion_id?: string
  access_count: number
  connection_count?: number
  // Source tracking
  source_title?: string
  source_url?: string
}

export interface LibraryHierarchyNode {
  id: string
  name: string
  type: 'domain' | 'node_type' | 'ingestion' | 'item'
  children?: LibraryHierarchyNode[]
  count?: number
  color?: string
  icon?: string
}

export interface LibraryConnection {
  id: string
  from_id: string
  from_name: string
  from_type: NodeType
  to_id: string
  to_name: string
  to_type: NodeType
  weight: number
  connection_type: string
}

export interface LibraryBacklink {
  id: string
  name: string
  type: NodeType
  domain?: Domain
  connection_weight: number
}

export interface LibraryState {
  items: LibraryItem[]
  filters: LibraryFilters
  sort: LibrarySort
  viewMode: LibraryViewMode
  selectedItemId: string | null
  expandedNodes: string[]
  loading: boolean
  error: Error | null
}

export const DEFAULT_FILTERS: LibraryFilters = {
  search: '',
  types: [],
  domains: [],
  minLevel: 1,
  maxLevel: 7
}

export const DEFAULT_SORT: LibrarySort = {
  field: 'created_at',
  direction: 'desc'
}

// Type colors for UI
export const TYPE_COLORS: Record<NodeType, string> = {
  atom: '#60A5FA',
  concept: '#A78BFA',
  chain: '#34D399',
  model: '#FBBF24',
  principle: '#F472B6',
  paradox: '#F87171',
  synthesis: '#38BDF8',
  ingestion: '#71717A'
}

export const TYPE_LABELS: Record<NodeType, string> = {
  atom: 'Atom',
  concept: 'Conceito',
  chain: 'Cadeia',
  model: 'Modelo',
  principle: 'Princípio',
  paradox: 'Paradoxo',
  synthesis: 'Síntese',
  ingestion: 'Ingestão'
}

export const TYPE_ICONS: Record<NodeType, string> = {
  atom: '⚛️',
  concept: '💡',
  chain: '🔗',
  model: '🏗️',
  principle: '⚖️',
  paradox: '🔄',
  synthesis: '✨',
  ingestion: '📥'
}
